import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A fingerprint of the photos a gallery shows, in order: the address of every main slide
 * image (and every grid image, for the grid layout). The zoom dialog is left out on
 * purpose - its images are swapped for high-resolution ones once zoomed, so including it
 * would make an untouched gallery look "different".
 *
 * @param {Element} root - A media gallery element.
 * @returns {string} The fingerprint, or an empty string when no image address could be read.
 */
function mediaFingerprint(root) {
  return Array.from(root.querySelectorAll('slideshow-slide img, .media-gallery__grid img'))
    .map((img) => img.getAttribute('src') || img.getAttribute('srcset') || img.dataset.src || '')
    .join('|');
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    // purekickz 2026-11-01: a finger on a photo is the moment to fetch its zoom view (see ZoomDialog.warm)
    this.addEventListener('pointerdown', this.#warmZoom, { signal, passive: true });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one -
   * but only when the new one actually shows different photos.
   *
   * Why the check: the theme re-fetches the page for every option click and used to swap the
   * whole gallery every time. When the visitor only changes size and every size shares the
   * same photos (the server renders the gallery from the selected variant's featured media,
   * so without one it is identical for all sizes), that swap re-creates every <img> and the
   * photo visibly flashes, while the page does a lot of pointless work: new DOM, new image
   * decoding, a new zoom dialog and listeners. Keeping the existing gallery also keeps the
   * slide the visitor is looking at instead of jumping back to the first photo.
   *
   * If the photos differ in any way (a variant with its own photo, a different number or
   * order of images, a combined-listing product), or the fingerprint cannot be read, the
   * original behaviour applies unchanged and the gallery is replaced.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const current = mediaFingerprint(this);
        if (current !== '' && current === mediaFingerprint(newMediaGallery)) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * @param {PointerEvent} event - The pointerdown event.
   */
  #warmZoom = (event) => {
    const zoom = this.refs.zoomDialogComponent;
    if (!zoom || typeof zoom.warm !== 'function' || !(event.target instanceof Element)) return;
    if (zoom.contains(event.target)) return;

    const items = this.slideshow?.slides ?? this.media ?? [];
    const index = Array.from(items).findIndex((item) => item.contains(event.target));
    zoom.warm(Math.max(index, 0));
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
